import { cx } from '@emotion/css';
import type { FC, ReactNode } from 'react';

import { MotifComponent, useMotifStyles } from '../../motif';
import type { BaseComponentProps, ImageSources } from '../../types';
import { dataSetToAttributes, formatDateToDatetime } from '../../utils';
import { Button, ButtonType } from '../Button';
import { Media } from '../Media';
import {
  editorialGalleryCardAnnotationCss,
  editorialGalleryCardBodyCss,
  editorialGalleryCardButtonCss,
  editorialGalleryCardClassName,
  editorialGalleryCardContainerCss,
  editorialGalleryCardContentCss,
  editorialGalleryCardDescriptionCss,
  editorialGalleryCardLayoutCss,
  editorialGalleryCardMediaClassName,
  editorialGalleryCardMediaContainerCss,
  editorialGalleryCardMediaCss,
  editorialGalleryCardMetadataCss,
  editorialGalleryCardPreviewCss,
  editorialGalleryCardTextGroupCss,
  editorialGalleryCardTitleCss,
  lensBrandBackgroundCss,
  noBrandBackgroundCss,
  snapBrandBackgroundCss,
  snapchatBrandBackgroundCss,
  spectaclesBrandBackgroundCss,
  stretchedLinkCss,
} from './EditorialGalleryCard.styles';

/**
 * Not linking to the @snapchat/mw-common Brand mappings since Lens isn't a brand supported via motif.
 */
export enum EditorialGalleryBrand {
  LENS = 'lens',
  SNAP = 'snap',
  SNAPCHAT = 'snapchat',
  SPECTACLES = 'spectacles',
}

export const editorialGalleryBrandNames: Record<EditorialGalleryBrand, string> = {
  [EditorialGalleryBrand.LENS]: 'Lens Studio',
  [EditorialGalleryBrand.SNAP]: 'Snap Inc.',
  [EditorialGalleryBrand.SNAPCHAT]: 'Snapchat',
  [EditorialGalleryBrand.SPECTACLES]: 'Spectacles',
};

const brandBackgroundCss: Record<EditorialGalleryBrand, string> = {
  [EditorialGalleryBrand.LENS]: lensBrandBackgroundCss,
  [EditorialGalleryBrand.SNAP]: snapBrandBackgroundCss,
  [EditorialGalleryBrand.SNAPCHAT]: snapchatBrandBackgroundCss,
  [EditorialGalleryBrand.SPECTACLES]: spectaclesBrandBackgroundCss,
};
export interface EditorialGalleryCardProps extends BaseComponentProps {
  date?: string;
  dateTime?: Date;
  brand?: EditorialGalleryBrand;
  title?: ReactNode;
  description?: ReactNode;
  link?: string;
  ctaLabel?: string;
  imgSrcs?: ImageSources;
  videoSource?: string;
  imgAltText?: string;
  isRtl?: boolean;
  onClick?: () => void;
  brandDataset?: DOMStringMap;
  titleDataset?: DOMStringMap;
  descriptionDataset?: DOMStringMap;
  dateDataset?: DOMStringMap;
  mediaDataset?: DOMStringMap;
}

/**
 * A SDS-M horizontal card component designed for editorial gallery layouts.
 *
 * @param {string} date - Formatted date string displayed in the card's metadata section.
 * @param {Date} dateTime - Date object used to generate the machine-readable `datetime` attribute
 *   on the `<time>` element. When omitted, the `datetime` attribute is not set.
 * @param {EditorialGalleryCardBrand} brand - Snap brand identity that determines the card's hover background color and
 *   the displayed brand label. When omitted, no brand label is rendered.
 * @param {ReactNode} title - Primary heading content rendered as an `<h5>`. Supports rich text
 *   (ReactNode). Hidden when omitted.
 * @param {ReactNode} description - Supporting body text rendered as a `<p>`. Only visible on
 *   non-mobile viewports. Hidden when omitted.
 * @param {string} link - URL for the card's CTA button. When provided, the entire card becomes
 *   clickable via the stretched-link pattern. When omitted, no CTA is rendered.
 * @param {string} ctaLabel - Label text for the CTA button. Also used as the button's `aria-label`
 *   for accessibility. Defaults to `'Read More'`.
 * @param {ImageSources} imgSrcs - Responsive image sources for the card's media thumbnail. Mutually
 *   exclusive with `videoSource` in practice, though both may be provided (image takes precedence
 *   in the Media component).
 * @param {string} videoSource - URL for a background video displayed in the card's media thumbnail.
 *   Used when `imgSrcs` is not provided.
 * @param {string} imgAltText - Alt text for the media image. Ignored when `videoSource` is used
 *   instead.
 * @param {boolean} isRtl - Whether the layout direction is right-to-left. Flips the CTA button's
 *   arrow icon from `arrow-right` to `arrow-left`. Defaults to `false`.
 * @param {() => void} onClick - Callback invoked when the CTA button is clicked. Typically used for
 *   analytics event logging.
 * @param {DOMStringMap} brandDataset - Contentful inspector `data-*` attributes applied to the
 *   brand label element.
 * @param {DOMStringMap} titleDataset - Contentful inspector `data-*` attributes applied to the
 *   title heading element.
 * @param {DOMStringMap} descriptionDataset - Contentful inspector `data-*` attributes applied to
 *   the description paragraph element.
 * @param {DOMStringMap} dateDataset - Contentful inspector `data-*` attributes applied to the
 *   `<time>` element.
 * @param {DOMStringMap} mediaDataset - Contentful inspector `data-*` attributes applied to the
 *   Media component.
 */
export const EditorialGalleryCard: FC<EditorialGalleryCardProps> = ({
  date,
  dateTime,
  title,
  description,
  imgSrcs,
  videoSource,
  imgAltText,
  brand,
  isRtl = false,
  link,
  ctaLabel = 'Read More',
  onClick,
  dataset,
  brandDataset,
  titleDataset,
  descriptionDataset,
  dateDataset,
  mediaDataset,
  className,
  ...props
}) => {
  useMotifStyles(MotifComponent.EDITORIAL_GALLERY_CARD);
  const hasMedia = imgSrcs || videoSource;

  return (
    <div
      className={cx(
        editorialGalleryCardContainerCss,
        brand ? brandBackgroundCss[brand] : noBrandBackgroundCss,
        className
      )}
      data-testid="editorial-gallery-card-container"
      {...dataSetToAttributes(dataset)}
      {...props}
    >
      <div className={cx(editorialGalleryCardClassName, editorialGalleryCardLayoutCss)}>
        <div className={editorialGalleryCardContentCss}>
          <div className={editorialGalleryCardMetadataCss}>
            {brand ? (
              <p
                className={editorialGalleryCardAnnotationCss}
                {...dataSetToAttributes(brandDataset)}
              >
                {editorialGalleryBrandNames[brand]}
              </p>
            ) : null}
            {date ? (
              <time
                dateTime={formatDateToDatetime(dateTime)}
                className={editorialGalleryCardAnnotationCss}
                {...dataSetToAttributes(dateDataset)}
              >
                {date}
              </time>
            ) : null}
          </div>
          <div className={editorialGalleryCardPreviewCss}>
            {hasMedia ? (
              <div className={editorialGalleryCardMediaContainerCss}>
                <Media
                  imgSrcs={imgSrcs}
                  altText={imgAltText}
                  videoSource={videoSource}
                  isBackgroundVideo={true}
                  className={cx(editorialGalleryCardMediaClassName, editorialGalleryCardMediaCss)}
                  dataset={mediaDataset}
                />
              </div>
            ) : null}
            <div className={editorialGalleryCardBodyCss}>
              {title || description ? (
                <div className={editorialGalleryCardTextGroupCss}>
                  {title ? (
                    <h5
                      className={editorialGalleryCardTitleCss}
                      {...dataSetToAttributes(titleDataset)}
                    >
                      {title}
                    </h5>
                  ) : null}
                  {description ? (
                    <p
                      className={editorialGalleryCardDescriptionCss}
                      {...dataSetToAttributes(descriptionDataset)}
                    >
                      {description}
                    </p>
                  ) : null}
                </div>
              ) : null}
              {link ? (
                <Button
                  type={ButtonType.Secondary}
                  iconName={isRtl ? 'arrow-left' : 'arrow-right'}
                  className={cx(editorialGalleryCardButtonCss, stretchedLinkCss)}
                  link={link}
                  onClick={onClick}
                >
                  {ctaLabel}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

EditorialGalleryCard.displayName = 'EditorialGalleryCard';
